import { Injectable, inject } from '@angular/core';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { AutoclassifierSdkService } from '../autoclassifier-sdk/autoclassifier-sdk.service';
import { TranscriptApiService } from '../transcript-api/transcript-api.service';
import { TranscriptMetadataService } from '../transcript-metadata/transcript-metadata.service';
import { TranscriptStatus } from '../../constants';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';

const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE_TRANSCRIPTS_MB_DEFAULT = 10;
const MAX_FILE_SIZE_TRANSCRIPTS_MB_ENHANCED = 50;
const MAX_CONCURRENT_UPLOADS = 10;

export interface FileUploadStatus {
  file: File;
  status: 'uploading' | 'completed' | 'failed';
  index: number;
  error?: string;
  id?: string;
}

export interface FileUploadResult {
  uploadedIds: string[];
  statuses: FileUploadStatus[];
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  private authService = inject(AuthService);
  private autoclassifierSdkService = inject(AutoclassifierSdkService);
  private transcriptApiService = inject(TranscriptApiService);
  private transcriptMetadataService = inject(TranscriptMetadataService);
  private featureFlagService = inject(FeatureFlagService);

  private statusSubject = new Subject<FileUploadStatus[]>();
  private errorSubject = new Subject<string>();

  /**
   * Uploads multiple files and returns observable of status updates
   * @param files - Array of files to upload
   * @param tenantId - The tenant identifier
   * @param isTranscriptFiling - Whether the files are transcript filings
   * @param typeId - The transcript type override (0 = College, 1 = High School)
   */
  uploadFiles(
    files: File[],
    tenantId: string,
    isTranscriptFiling: boolean,
    typeId?: number | null
  ): Observable<FileUploadResult> {
    return new Observable(observer => {
      const fileStatuses: FileUploadStatus[] = files.map((file, index) => ({
        file,
        status: 'uploading',
        index
      }));

      const uploadedIds: string[] = new Array(files.length);
      let processedCount = 0;
      let nextIndex = 0;

      const emitUpdate = () => {
        const orderedUploadedIds = uploadedIds.filter(id => id !== undefined);
        observer.next({ uploadedIds: orderedUploadedIds, statuses: [...fileStatuses] });
      };

      const processNext = () => {
        if (nextIndex >= files.length) return;

        const index = nextIndex++;
        this.processFile(fileStatuses[index].file, tenantId, isTranscriptFiling, typeId)
          .then(result => {
            if (result.success) {
              uploadedIds[index] = result.id;
              fileStatuses[index].status = 'completed';
              fileStatuses[index].id = result.id;
            } else {
              fileStatuses[index].status = 'failed';
              fileStatuses[index].error = result.error;
            }
          })
          .catch(error => {
            fileStatuses[index].status = 'failed';
            fileStatuses[index].error = error.message;
          })
          .finally(() => {
            processedCount++;
            emitUpdate();
            if (processedCount === files.length) {
              observer.complete();
            } else {
              processNext();
            }
          });
      };

      // Initial emit
      emitUpdate();

      const initialBatch = Math.min(MAX_CONCURRENT_UPLOADS, files.length);
      for (let i = 0; i < initialBatch; i++) {
        processNext();
      }
    });
  }

  /**
   * Processes a single file upload
   * @param file - The file to process
   * @param tenantId - The tenant identifier
   * @param isTranscriptFiling - Whether the file is a transcript filing
   * @param typeId - The transcript type override (0 = College, 1 = High School)
   */
  private async processFile(
    file: File,
    tenantId: string,
    isTranscriptFiling: boolean,
    typeId?: number | null
  ): Promise<{ success: true; id: string } | { success: false; error: string }> {
    let transcriptMaxMb = MAX_FILE_SIZE_TRANSCRIPTS_MB_DEFAULT;
    if (isTranscriptFiling) {
      const flag = await firstValueFrom(
        this.featureFlagService.getFlag('feature-itp-50-mb-support', false)
      );
      transcriptMaxMb = flag
        ? MAX_FILE_SIZE_TRANSCRIPTS_MB_ENHANCED
        : MAX_FILE_SIZE_TRANSCRIPTS_MB_DEFAULT;
    }
    const maxSizeInBytes = isTranscriptFiling
      ? transcriptMaxMb * 1024 * 1024
      : MAX_FILE_SIZE_MB * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      return {
        success: false,
        error: `File too large. ${file.name} exceeds the ${
          isTranscriptFiling ? transcriptMaxMb : MAX_FILE_SIZE_MB
        }MB size limit.`
      };
    }

    if (!tenantId) {
      return { success: false, error: 'Tenant ID is required' };
    }

    if (isTranscriptFiling && !this.isValidTranscriptFile(file)) {
      return { success: false, error: `Invalid file type. ${file.name} must be PDF.` };
    }

    try {
      if (isTranscriptFiling) {
        return await this.uploadTranscript(file, tenantId, typeId);
      } else {
        return await this.uploadDocument(file, tenantId);
      }
    } catch (error) {
      return { success: false, error: `Failed to upload ${file.name}` };
    }
  }

  /**
   * Validates if file is acceptable for transcript filing
   * @param file - The file to validate
   */
  private isValidTranscriptFile(file: File): boolean {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const allowedTypes = ['pdf'];
    return allowedTypes.includes(fileExtension || '') || !!file.type.match(/^(application\/pdf)$/);
  }

  /**
   * Uploads a transcript file
   * @param file - The transcript file to upload
   * @param tenantId - The tenant identifier
   * @param typeId - The transcript type override (0 = College, 1 = High School)
   */
  private async uploadTranscript(
    file: File,
    tenantId: string,
    typeId?: number | null
  ): Promise<{ success: true; id: string }> {
    const authToken = this.authService.getAuthToken()!;

    try {
      const placeholderDocumentId = this.generateUUID();
      const createResponse =
        await this.autoclassifierSdkService.transcriptMetaDataSdk.createTranscriptMetadata(
          authToken,
          tenantId,
          placeholderDocumentId,
          'transcriptProcessing',
          file.name,
          typeId
        );

      const transcriptId = createResponse.transId || createResponse.trans_id;

      if (!transcriptId) {
        throw new Error('Transcript ID not returned from API');
      }

      const presignedUrlResponse = await firstValueFrom(
        this.transcriptApiService.getPresignedUrl(file.name, tenantId, transcriptId)
      );

      await firstValueFrom(
        this.transcriptApiService.uploadFileToS3(
          file,
          presignedUrlResponse.upload_url,
          presignedUrlResponse.content_type
        )
      );

      await this.transcriptMetadataService.enqueueTranscript(
        presignedUrlResponse.s3_key,
        presignedUrlResponse.s3_bucket,
        tenantId,
        transcriptId
      );

      return { success: true, id: transcriptId };
    } catch (error) {
      console.error('Transcript upload failed:', error);
      throw error;
    }
  }

  // TODO: Replace this server side before deployment to prod
  /**
   * Generates a UUID v4
   * @returns A UUID string
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Uploads a regular document
   * @param file - The document file to upload
   * @param tenantId - The tenant identifier
   */
  private async uploadDocument(
    file: File,
    tenantId: string
  ): Promise<{ success: true; id: string }> {
    const authToken = this.authService.getAuthToken()!;

    const response = await this.autoclassifierSdkService.documentManagementSdk.uploadDocument(
      tenantId,
      file,
      undefined,
      undefined,
      authToken
    );

    return { success: true, id: response.id };
  }

  /**
   * Handles file input change events
   * @param event - The input change event
   * @param tenantId - The tenant identifier
   * @param isTranscriptFiling - Whether the files are transcript filings
   * @param typeId - The transcript type override (0 = College, 1 = High School)
   */
  handleFileInputChange(
    event: Event,
    tenantId: string,
    isTranscriptFiling: boolean,
    typeId?: number | null
  ): Observable<FileUploadResult> {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      return this.uploadFiles(Array.from(input.files), tenantId, isTranscriptFiling, typeId);
    }
    return new Observable(observer => observer.complete());
  }

  /**
   * Handles drag and drop events
   * @param event - The drag event
   * @param tenantId - The tenant identifier
   * @param isTranscriptFiling - Whether the files are transcript filings
   * @param typeId - The transcript type override (0 = College, 1 = High School)
   */
  handleDrop(
    event: DragEvent,
    tenantId: string,
    isTranscriptFiling: boolean,
    typeId?: number | null
  ): Observable<FileUploadResult> {
    event.preventDefault();
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length) {
      return this.uploadFiles(files, tenantId, isTranscriptFiling, typeId);
    }
    return new Observable(observer => observer.complete());
  }

  /**
   * Handles drag over events
   * @param event - The drag over event
   */
  handleDragOver(event: DragEvent): void {
    event.preventDefault();
  }
}
